import { useCallback, useEffect, useMemo, useState } from 'react';
import { appendCampaignToUrl } from '#/utils/campaignContext';
import { track } from '#/utils/analytics';
import { iconUrl } from '#/utils/assetUrl';
import { DEEPLINK_BENEFIT_CTA } from '#/constants/deeplinks';
import { useBenefitsLatest } from '#/domains/preview/hooks/useBenefitsLatest';
import { useBenefitCategories } from '#/domains/preview/hooks/useBenefitCategories';
import { useOverlayBackClose } from '#/domains/preview/hooks/useOverlayBackClose';
import BenefitDetailModal from '#/domains/preview/components/BenefitDetailModal';
import CategoryFilterSheet from '#/domains/preview/components/CategoryFilterSheet';
import './BenefitsPreviewPage.css';

const TOTAL_BENEFITS_FALLBACK = 10000;
const INITIAL_VISIBLE_COUNT = 7;
const LOAD_MORE_STEP = 5;

const SIDO_LIST: ReadonlyArray<{ code: string; displayName: string }> = [
  { code: '서울특별시', displayName: '서울특별시' },
  { code: '부산광역시', displayName: '부산광역시' },
  { code: '대구광역시', displayName: '대구광역시' },
  { code: '인천광역시', displayName: '인천광역시' },
  { code: '광주광역시', displayName: '광주광역시' },
  { code: '대전광역시', displayName: '대전광역시' },
  { code: '울산광역시', displayName: '울산광역시' },
  { code: '세종특별자치시', displayName: '세종특별자치시' },
  { code: '경기도', displayName: '경기도' },
  { code: '강원특별자치도', displayName: '강원특별자치도' },
  { code: '충청북도', displayName: '충청북도' },
  { code: '충청남도', displayName: '충청남도' },
  { code: '전북특별자치도', displayName: '전북특별자치도' },
  { code: '전라남도', displayName: '전라남도' },
  { code: '경상북도', displayName: '경상북도' },
  { code: '경상남도', displayName: '경상남도' },
  { code: '제주특별자치도', displayName: '제주특별자치도' },
];

export default function BenefitsPreviewPage() {
  useEffect(() => {
    track('service_entry_pv', {
      event_key: 'gov_benefit_all_preview_landing_pv',
      service_code: 'GOV_BENEFIT',
    });
  }, []);

  const ctaHref = appendCampaignToUrl(DEEPLINK_BENEFIT_CTA);

  const { categories } = useBenefitCategories();
  const [selectedCategoryCodes, setSelectedCategoryCodes] = useState<string[]>(
    []
  );
  const [selectedRegions, setSelectedRegions] = useState<string[]>([]);
  const [openSheet, setOpenSheet] = useState<'category' | 'region' | null>(
    null
  );

  const {
    items,
    totalElements,
    hasMore,
    isInitialLoading,
    isLoadingMore,
    error,
    loadMore,
    retry,
  } = useBenefitsLatest(selectedCategoryCodes, selectedRegions, '');

  const headlineCount =
    totalElements > 0 ? totalElements : TOTAL_BENEFITS_FALLBACK;

  const [visibleCount, setVisibleCount] = useState(INITIAL_VISIBLE_COUNT);

  useEffect(() => {
    setVisibleCount(INITIAL_VISIBLE_COUNT);
  }, [selectedCategoryCodes, selectedRegions]);

  const visibleItems = useMemo(
    () => items.slice(0, visibleCount),
    [items, visibleCount]
  );

  const canRevealMoreLocal = items.length > visibleCount;
  const showLoadMoreButton = canRevealMoreLocal || hasMore;

  const selectedCategoryNames = useMemo(() => {
    if (selectedCategoryCodes.length === 0) return [];
    const set = new Set(selectedCategoryCodes);
    return categories.filter(c => set.has(c.code)).map(c => c.displayName);
  }, [categories, selectedCategoryCodes]);

  const categoryChipLabel =
    selectedCategoryNames.length === 0
      ? '혜택 분야 선택'
      : selectedCategoryNames.length === 1
        ? selectedCategoryNames[0]
        : `${selectedCategoryNames[0]} 외 ${selectedCategoryNames.length - 1}`;

  const regionChipLabel =
    selectedRegions.length === 0
      ? '지역 선택'
      : selectedRegions.length === 1
        ? selectedRegions[0]
        : `${selectedRegions[0]} 외 ${selectedRegions.length - 1}`;

  const handleOpenCategoryFilter = () => {
    track('gov_benefit_all_preview_landing_filter_open', {
      filter: 'category',
    });
    setOpenSheet('category');
  };

  const handleOpenRegionFilter = () => {
    track('gov_benefit_all_preview_landing_filter_open', { filter: 'region' });
    setOpenSheet('region');
  };

  const handleApplyCategoryFilter = (codes: string[]) => {
    track('gov_benefit_all_preview_landing_filter_apply', {
      filter: 'category',
      category_count: codes.length,
      category_codes: codes.join(',') || 'none',
    });
    setSelectedCategoryCodes(codes);
  };

  const handleApplyRegionFilter = (regs: string[]) => {
    track('gov_benefit_all_preview_landing_filter_apply', {
      filter: 'region',
      region_count: regs.length,
      regions: regs.join(',') || 'none',
    });
    setSelectedRegions(regs);
  };

  const anyFilterActive =
    selectedCategoryCodes.length > 0 || selectedRegions.length > 0;

  const handleCtaClick = () => {
    track('service_conversion_sub', {
      event_key: 'benefits_preview_cta_sticky',
    });
  };

  const [selectedBenefit, setSelectedBenefit] = useState<{
    serviceSeqNo: string;
    serviceName: string;
  } | null>(null);

  const handleBenefitClick = (serviceSeqNo: string, serviceName: string) => {
    track('gov_benefit_all_preview_landing_item_click', {
      service_seq_no: serviceSeqNo,
    });
    setSelectedBenefit({ serviceSeqNo, serviceName });
  };

  const closeSelectedBenefit = useCallback(() => setSelectedBenefit(null), []);
  const closeOpenSheet = useCallback(() => setOpenSheet(null), []);

  useOverlayBackClose(selectedBenefit !== null, closeSelectedBenefit);
  useOverlayBackClose(openSheet !== null, closeOpenSheet);

  const handleLoadMore = () => {
    track('gov_benefit_all_preview_landing_load_more_click');
    if (canRevealMoreLocal) {
      setVisibleCount(c => c + LOAD_MORE_STEP);
      return;
    }
    if (hasMore) {
      setVisibleCount(c => c + LOAD_MORE_STEP);
      loadMore();
    }
  };

  return (
    <div className="bp-shell">
      <div className="bp-frame">
        {/* ===== Hero ===== */}
        <section className="bp-hero">
          <p className="bp-hero-kicker">
            나라에서 어떤 혜택이나 지원을 제공할까?
          </p>
          <h1 className="bp-hero-title">
            <span className="bp-hero-num">전국 약 1만 개 이상의</span>
            <br />
            정부 혜택을 확인해보세요
          </h1>
          <p className="bp-hero-kicker bp-hero-kicker--subtle">
            혜택알리미 데이터 기반으로 알려드려요
          </p>
        </section>

        {/* ===== Benefit list (최신등록순) ===== */}
        <section className="bp-list-section">
          <div className="bp-filter-row-chips">
            <button
              type="button"
              className={
                selectedCategoryCodes.length > 0
                  ? 'bp-filter-chip bp-filter-chip--active'
                  : 'bp-filter-chip'
              }
              onClick={handleOpenCategoryFilter}
            >
              <span className="bp-filter-chip-label">{categoryChipLabel}</span>
              <svg
                className="bp-filter-chip-caret"
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                aria-hidden
              >
                <path
                  d="M6 9l6 6 6-6"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
            <button
              type="button"
              className={
                selectedRegions.length > 0
                  ? 'bp-filter-chip bp-filter-chip--active'
                  : 'bp-filter-chip'
              }
              onClick={handleOpenRegionFilter}
            >
              <span className="bp-filter-chip-label">{regionChipLabel}</span>
              <svg
                className="bp-filter-chip-caret"
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                aria-hidden
              >
                <path
                  d="M6 9l6 6 6-6"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </div>

          <p className="bp-list-count">
            {anyFilterActive ? (
              <>
                선택한 조건{' '}
                <span className="bp-list-count-num">
                  {totalElements.toLocaleString()}개
                </span>
              </>
            ) : (
              <>
                전체{' '}
                <span className="bp-list-count-num">
                  {headlineCount.toLocaleString()}개
                </span>
              </>
            )}
          </p>

          {isInitialLoading ? (
            <ul className="bp-list bp-list--skeleton" aria-hidden>
              {Array.from({ length: 5 }).map((_, i) => (
                <li key={i} className="bp-list-item bp-list-item--skeleton">
                  <div className="bp-list-item-link">
                    <div className="bp-list-item-top">
                      <div className="bp-list-thumb bp-skeleton-box" />
                    </div>
                    <div className="bp-skeleton-line bp-skeleton-line--lg" />
                    <div className="bp-skeleton-line bp-skeleton-line--md" />
                    <div className="bp-skeleton-line bp-skeleton-line--sm" />
                  </div>
                </li>
              ))}
            </ul>
          ) : error ? (
            <div className="bp-list-error">
              <p>{error}</p>
              <button type="button" className="bp-list-retry" onClick={retry}>
                다시 시도
              </button>
            </div>
          ) : (
            <>
              {items.length === 0 && anyFilterActive && (
                <div className="bp-list-empty">
                  <p>선택한 조건의 혜택이 아직 등록되지 않았어요.</p>
                  <button
                    type="button"
                    className="bp-list-empty-action"
                    onClick={() => {
                      handleApplyCategoryFilter([]);
                      handleApplyRegionFilter([]);
                    }}
                  >
                    전체 보기
                  </button>
                </div>
              )}
              <ul className="bp-list">
                {visibleItems.map(b => (
                  <li key={b.benefitId} className="bp-list-item">
                    <button
                      type="button"
                      className="bp-list-item-link"
                      onClick={() =>
                        handleBenefitClick(b.serviceSeqNo, b.serviceName)
                      }
                    >
                      <div className="bp-list-thumb">
                        {b.imageUrl ? (
                          <img
                            src={b.imageUrl}
                            alt=""
                            loading="lazy"
                            className="bp-list-thumb-img"
                          />
                        ) : (
                          <span className="bp-list-thumb-fallback">혜택</span>
                        )}
                      </div>
                      <div className="bp-list-item-body">
                        <p className="bp-list-title">{b.serviceName}</p>
                        {b.introductionContent && (
                          <p className="bp-list-desc">
                            {b.introductionContent}
                          </p>
                        )}
                        {(b.applyFormat || b.competentOrgName) && (
                          <div className="bp-list-tags">
                            {b.applyFormat && (
                              <span className="bp-list-tag">
                                {b.applyFormat}
                              </span>
                            )}
                            {b.competentOrgName && (
                              <span className="bp-list-tag">
                                {b.competentOrgName}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                      <svg
                        className="bp-list-chevron"
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        aria-hidden
                      >
                        <path
                          d="M9 6l6 6-6 6"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                  </li>
                ))}
              </ul>

              {showLoadMoreButton && items.length > 0 && (
                <button
                  type="button"
                  className="bp-list-more"
                  onClick={handleLoadMore}
                  disabled={isLoadingMore}
                >
                  {isLoadingMore ? '불러오는 중…' : '더 많은 혜택 보기'}
                </button>
              )}
            </>
          )}
        </section>

        {/* ===== Pivot ===== */}
        <section className="bp-pivot">
          <h2 className="bp-pivot-title">
            그런데 이 많은 혜택 중에서
            <br />
            <span className="bp-pivot-emph">
              내가 진짜 받을 수 있는 건 몇 개일까요?
            </span>
          </h2>
          <p className="bp-pivot-sub">
            자격 조건을 하나하나 따져보긴 너무 어려워요.
            <br />
            혜택알리미가 대신 찾아드릴게요.
          </p>

          <div className="bp-features">
            <div className="bp-feature">
              <img
                src={iconUrl('feat-magnifier.png')}
                alt=""
                className="bp-feature-icon"
              />
              <div>
                <h3 className="bp-feature-title">내 상황에 딱 맞는 혜택만</h3>
                <p className="bp-feature-desc">
                  나이, 지역, 직업, 가구 상황을 분석해서 받을 수 있는 혜택만 콕
                  집어 알려드려요.
                </p>
              </div>
            </div>
            <div className="bp-feature">
              <img
                src={iconUrl('feat-bell-alarm.png')}
                alt=""
                className="bp-feature-icon"
              />
              <div>
                <h3 className="bp-feature-title">
                  새로운 혜택, 놓치지 않게 알림
                </h3>
                <p className="bp-feature-desc">
                  새 혜택이 생기면 푸시로 알려드려요. 신청 기간 놓칠 일 없어요.
                </p>
              </div>
            </div>
            <div className="bp-feature">
              <img
                src={iconUrl('feat-hand-check.png')}
                alt=""
                className="bp-feature-icon"
              />
              <div>
                <h3 className="bp-feature-title">확인부터 신청까지 한 번에</h3>
                <p className="bp-feature-desc">
                  복잡한 정부 사이트 돌아다닐 필요 없이 삼쩜삼 앱 하나로 끝나요.
                </p>
              </div>
            </div>
          </div>
        </section>
      </div>

      {/* ===== Sticky CTA ===== */}
      <div className="bp-cta">
        <div className="bp-cta-inner">
          <a
            id="btn-sticky-cta"
            href={ctaHref}
            onClick={handleCtaClick}
            target="_blank"
            rel="noopener noreferrer"
            className="bp-cta-button"
          >
            맞춤 혜택 1분 안에 확인하기
          </a>
        </div>
      </div>

      {selectedBenefit && (
        <BenefitDetailModal
          serviceSeqNo={selectedBenefit.serviceSeqNo}
          serviceName={selectedBenefit.serviceName}
          onClose={() => setSelectedBenefit(null)}
        />
      )}

      {openSheet === 'category' && (
        <CategoryFilterSheet
          title="혜택 분야 선택"
          categories={categories}
          selected={selectedCategoryCodes}
          onApply={handleApplyCategoryFilter}
          onClose={() => setOpenSheet(null)}
        />
      )}

      {openSheet === 'region' && (
        <CategoryFilterSheet
          title="지역 선택"
          categories={[...SIDO_LIST]}
          selected={selectedRegions}
          onApply={handleApplyRegionFilter}
          onClose={() => setOpenSheet(null)}
        />
      )}
    </div>
  );
}
